import { useState } from 'react'
import { toast } from 'sonner'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PageHeader } from '@/components/layout/PageHeader'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { CompanyDetailsForm } from '@/components/settings/CompanyDetailsForm'
import { CompanySettingsSkeleton } from '@/components/settings/CompanySettingsSkeleton'

export function CompanySettingsPage() {
  const {
    currentCompany,
    updateCompany,
    isCompanyLoading,
    isAuthLoading,
    isInitialized,
  } = useAppAuth()
  const [saving, setSaving] = useState(false)

  const onSubmit = async (values: any) => {
    if (!currentCompany?.id) return false

    setSaving(true)
    try {
      // Filter out empty/null values and normalize strings before sending to API.
      const updateData = Object.entries(values).reduce((acc, [key, value]) => {
        if (value === undefined || value === null) return acc
        if (typeof value === 'string') {
          const trimmedValue = value.trim()
          if (trimmedValue === '') return acc
          acc[key] = trimmedValue
          return acc
        }
        acc[key] = value
        return acc
      }, {} as any)

      await updateCompany(currentCompany.id, updateData)
      toast.success(
        `Company details updated successfully for ${updateData.name}`,
      )
      return true // Success
    } catch (error) {
      console.error('Error updating company:', error)
      toast.error('Failed to update company details')
      return false // Failure
    } finally {
      setSaving(false)
    }
  }

  // Show nothing while initial auth is loading (let the auth overlay handle this)
  if (isAuthLoading || !isInitialized) {
    return null
  }

  // Show skeleton during company loading or when no company data
  if (isCompanyLoading || !currentCompany) {
    return <CompanySettingsSkeleton />
  }

  return (
    <div className="h-full pt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0 pb-24 mt-6">
          <PageHeader title="Manage your company details and preferences" />

          <CompanyDetailsForm
            currentCompany={currentCompany}
            isCompanyLoading={isCompanyLoading}
            onSubmit={onSubmit}
            saving={saving}
          />
        </div>
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}
